/**
 * SaveAsTemplateModalCN — shared modal for snapshotting a workspace's current
 * items as a new reusable template. Used by every evaluation tab (Scoring,
 * Financials × 3, Risk, Pros/Cons).
 *
 * The caller hands over `sourceItems` (and optionally `defaults` + `modelType`
 * for financial categories). The modal collects name/description/accessLevel
 * and POSTs to the right `/api/templates/build-*` endpoint per templateType.
 *
 * Linear: MYD-318 (foundation). Build endpoints will 404 until each
 * category's backend issue lands (scoring → MYD-319, financials → MYD-322,
 * risk → MYD-329, pros/cons → MYD-332). Server errors are surfaced verbatim.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import axios from 'axios'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from 'components/ui/dialog'
import { Button } from 'components/ui/button'
import { Input } from 'components/ui/input'
import { Label } from 'components/ui/label'
import { Badge } from 'components/ui/badge'
import {
	Save,
	Loader2,
	AlertTriangle,
} from 'lucide-react'
import { cn } from 'lib/utils'
import type {
	EvaluationTemplateType,
	SaveAsTemplateModalProps,
} from './types'

const BUILD_URL: Record<EvaluationTemplateType, string> = {
	scoring: '/api/templates/build-scoring-criteria',
	acquisition: '/api/templates/build-financial-line-items',
	operating: '/api/templates/build-financial-line-items',
	roi: '/api/templates/build-financial-line-items',
	risk: '/api/templates/build-risks',
	prosCons: '/api/templates/build-pros-cons',
}

const TYPE_LABEL: Record<EvaluationTemplateType, string> = {
	scoring: 'scoring',
	acquisition: 'acquisition',
	operating: 'operating',
	roi: 'ROI analysis',
	risk: 'risk',
	prosCons: 'pros & cons',
}

const SaveAsTemplateModalCN = ({
	open,
	onOpenChange,
	templateType,
	sourceItems,
	defaults,
	modelType,
	onSaved,
}: SaveAsTemplateModalProps) => {
	const token = useSelector((s: any) => s?.userLogin?.userInfo?.token)
	const authConfig = useMemo(
		() => (token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
		[token],
	)

	const [templateName, setTemplateName] = useState('')
	const [description, setDescription] = useState('')
	const [accessLevel, setAccessLevel] = useState<'private' | 'org-wide' | 'public'>(
		'private',
	)
	const [saving, setSaving] = useState(false)
	const [error, setError] = useState<string | null>(null)

	// Reset on open so a previous attempt's state never leaks back in.
	useEffect(() => {
		if (!open) return
		setTemplateName('')
		setDescription('')
		setAccessLevel('private')
		setError(null)
	}, [open])

	const canSave = templateName.trim().length > 0 && sourceItems.length > 0 && !saving

	const handleSave = useCallback(async () => {
		if (!canSave) return
		setSaving(true)
		setError(null)
		try {
			// Financial categories store all three under templateType='acquisition' |
			// 'operating' | 'roi'; the backend needs `modelType` to route to the
			// right SiteFinancial.modelType when the template is applied later.
			const resolvedModelType =
				modelType ??
				(templateType === 'acquisition' ||
				templateType === 'operating' ||
				templateType === 'roi'
					? templateType
					: undefined)

			const { data } = await axios.post(
				BUILD_URL[templateType],
				{
					templateName: templateName.trim(),
					description: description.trim() || undefined,
					accessLevel,
					items: sourceItems,
					...(defaults ? { defaults } : {}),
					...(resolvedModelType ? { modelType: resolvedModelType } : {}),
				},
				authConfig,
			)
			onSaved?.(data?._id || data?.id, data?.templateName || templateName.trim())
			onOpenChange(false)
		} catch (err: any) {
			setError(
				err?.response?.data?.message || err?.message || 'Failed to save template',
			)
		} finally {
			setSaving(false)
		}
	}, [
		canSave,
		templateType,
		templateName,
		description,
		accessLevel,
		sourceItems,
		defaults,
		modelType,
		authConfig,
		onSaved,
		onOpenChange,
	])

	return (
		<Dialog open={open} onClose={() => onOpenChange(false)}>
			<DialogContent className="max-w-md flex flex-col">
				<DialogHeader>
					<div className="min-w-0">
						<DialogTitle className="flex items-center gap-2">
							<Save className="w-5 h-5 text-[#0080fe]" />
							Save as {TYPE_LABEL[templateType]} template
						</DialogTitle>
						<DialogDescription>
							Snapshot the current {sourceItems.length} item
							{sourceItems.length === 1 ? '' : 's'} into a reusable template.
						</DialogDescription>
					</div>
				</DialogHeader>

				<div className="px-5 py-4 space-y-4">
					{sourceItems.length === 0 ? (
						<div className="flex items-start gap-2 px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-800">
							<AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
							<span>
								There are no items to save. Add at least one before snapshotting as a
								template.
							</span>
						</div>
					) : (
						<>
							<div>
								<Label htmlFor="tplName" className="mb-1.5 block">
									Template name *
								</Label>
								<Input
									id="tplName"
									value={templateName}
									onChange={(e) => setTemplateName(e.target.value)}
									placeholder={defaultPlaceholder(templateType)}
									autoFocus
								/>
							</div>

							<div>
								<Label htmlFor="tplDesc" className="mb-1.5 block">
									Description (optional)
								</Label>
								<textarea
									id="tplDesc"
									value={description}
									onChange={(e) => setDescription(e.target.value)}
									placeholder="What's this template for? When should it be reused?"
									rows={3}
									className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0080fe]/20 focus:border-[#0080fe]"
								/>
							</div>

							<div>
								<Label className="mb-1.5 block">Visibility</Label>
								<div className="grid grid-cols-3 gap-1.5">
									{(['private', 'org-wide', 'public'] as const).map((level) => (
										<button
											key={level}
											type="button"
											onClick={() => setAccessLevel(level)}
											className={cn(
												'px-2 py-1.5 rounded-md text-xs border transition-colors',
												accessLevel === level
													? 'border-[#0080fe] bg-[#0080fe]/5 text-[#0080fe] font-medium'
													: 'border-gray-200 text-gray-600 hover:border-gray-300',
											)}
										>
											{level === 'org-wide' ? 'Org-wide' : level.charAt(0).toUpperCase() + level.slice(1)}
										</button>
									))}
								</div>
							</div>

							{defaults && Object.keys(defaults).length > 0 && (
								<div className="rounded-md bg-gray-50 px-3 py-2.5">
									<div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
										Captured assumptions
									</div>
									<div className="flex flex-wrap gap-1.5">
										{Object.entries(defaults).map(([k, v]) => (
											<Badge
												key={k}
												className="text-[10px] bg-white border border-gray-200 text-gray-700"
											>
												{k}: {String(v)}
											</Badge>
										))}
									</div>
								</div>
							)}

							{error && (
								<div className="flex items-start gap-2 px-3 py-2 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">
									<AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
									<span>{error}</span>
								</div>
							)}
						</>
					)}
				</div>

				<DialogFooter className="px-5 pb-4 pt-2 gap-2">
					<Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>
						Cancel
					</Button>
					<Button
						variant="primary"
						onClick={handleSave}
						disabled={!canSave}
						className="gap-1.5"
					>
						{saving ? (
							<>
								<Loader2 className="w-4 h-4 animate-spin" />
								Saving…
							</>
						) : (
							<>
								<Save className="w-4 h-4" />
								Save template
							</>
						)}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}

function defaultPlaceholder(t: EvaluationTemplateType): string {
	switch (t) {
		case 'scoring':
			return 'e.g. Retail QSR score — 2026'
		case 'acquisition':
			return 'e.g. Single-tenant build-to-suit'
		case 'operating':
			return 'e.g. 5-year retail P&L'
		case 'roi':
			return 'e.g. Standard 5-year ROI'
		case 'risk':
			return 'e.g. Common CRE risk register'
		case 'prosCons':
			return 'e.g. Retail site pros & cons starter'
	}
}

export default SaveAsTemplateModalCN
